import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, X, Music, Send, CheckCircle, Clock, XCircle, Upload, Image, ShoppingBag, Calendar, MapPin, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENRE_OPTIONS = [
  'Rock', 'Indie', 'Alternative', 'Punk', 'Post-Punk', 'Shoegaze',
  'Metal', 'Garage Rock', 'Blues Rock', 'Grunge', 'Lo-Fi',
  'Synth-Punk', 'Industrial', 'Electronic', 'Darkwave', 'Noise Rock',
  'Psych Rock', 'Folk', 'Americana', 'Hardcore', 'Emo', 'Honky Tonk', 'Country'
];

const BandApply = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [existingApplications, setExistingApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '', // Band contact email
    bio: '',
    genre_tags: [],
    location: '',
    website: '',
    upcoming_shows: [{ venue: '', location: '', date: '', time: '', cost: '' }],
    merch_items: [{ name: '', url: '', description: '' }], // Multiple merch
    donate_link: '',
    logo_url: '', // Logo URL
    image_urls: [''], // Multiple images
    embed_tracks: [{ title: '', embed_url: '', type: 'single' }]
  });

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const res = await fetch(`${API}/applications/my`, {
          headers: getAuthHeaders(),
          
        });
        if (res.ok) {
          setExistingApplications(await res.json());
        }
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchApplications();
  }, [getAuthHeaders]);

  const toggleGenre = (genre) => {
    setFormData(prev => ({
      ...prev,
      genre_tags: prev.genre_tags.includes(genre)
        ? prev.genre_tags.filter(g => g !== genre)
        : [...prev.genre_tags, genre]
    }));
  };

  // Track handlers
  const addTrack = () => {
    setFormData(prev => ({
      ...prev,
      embed_tracks: [...prev.embed_tracks, { title: '', embed_url: '', type: 'single' }]
    }));
  };

  const removeTrack = (index) => {
    setFormData(prev => ({
      ...prev,
      embed_tracks: prev.embed_tracks.filter((_, i) => i !== index)
    }));
  };

  const updateTrack = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      embed_tracks: prev.embed_tracks.map((track, i) => 
        i === index ? { ...track, [field]: value } : track
      )
    }));
  };

  // Image handlers
  const addImage = () => {
    if (formData.image_urls.length >= 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }
    setFormData(prev => ({
      ...prev,
      image_urls: [...prev.image_urls, '']
    }));
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, i) => i !== index)
    }));
  };

  const updateImage = (index, value) => {
    setFormData(prev => ({
      ...prev,
      image_urls: prev.image_urls.map((url, i) => i === index ? value : url)
    }));
  };

  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e, type, index = null) => {
    const file = e.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size, 'bytes');

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, or WebP image');
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Image must be less than 50MB');
      return;
    }

    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      // Get auth token from localStorage directly as backup
      const authHeaders = getAuthHeaders();
      const storedToken = localStorage.getItem('token');
      const headers = {};
      
      // Use auth headers or fallback to stored token
      if (authHeaders && authHeaders['Authorization']) {
        headers['Authorization'] = authHeaders['Authorization'];
      } else if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const uploadUrl = `${API}/upload/image`;
      console.log('Uploading to:', uploadUrl);
      console.log('Auth token present:', !!headers['Authorization']);
      
      if (!headers['Authorization']) {
        toast.error('Please log in again to upload images');
        return;
      }
      
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: headers,
        body: formDataUpload
      });

      console.log('Upload response:', res.status, res.statusText);

      if (res.ok) {
        const data = await res.json();
        console.log('Upload success:', data);
        if (type === 'logo') {
          setFormData(prev => ({ ...prev, logo_url: data.url }));
        } else if (type === 'image' && index !== null) {
          updateImage(index, data.url);
        }
        toast.success('Image uploaded!');
      } else {
        // Clone the response to read it safely (avoids "body stream already read" error)
        const resClone = res.clone();
        let errorMsg = `Upload failed (${res.status})`;
        try {
          const errorData = await resClone.json();
          errorMsg = errorData.detail || errorMsg;
        } catch {
          try {
            const errorText = await res.text();
            errorMsg = errorText || errorMsg;
          } catch {
            // Use default error message
          }
        }
        console.error('Upload error:', res.status, errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Upload exception:', error.name, error.message);
      console.error('Full error:', error);
      toast.error(`Upload failed: ${error.message || 'Network error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteUploadedImage = async (url, type, index = null) => {
    // Extract filename from URL
    const filename = url.split('/').pop();
    
    try {
      await fetch(`${API}/upload/image/${filename}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (type === 'logo') {
        setFormData(prev => ({ ...prev, logo_url: '' }));
      } else if (type === 'image' && index !== null) {
        removeImage(index);
      }
      toast.success('Image removed');
    } catch (error) {
      // Still remove from form even if delete fails
      if (type === 'logo') {
        setFormData(prev => ({ ...prev, logo_url: '' }));
      } else if (type === 'image' && index !== null) {
        removeImage(index);
      }
    }
  };

  // Merch handlers
  const addMerchItem = () => {
    setFormData(prev => ({
      ...prev,
      merch_items: [...prev.merch_items, { name: '', url: '', description: '' }]
    }));
  };

  const removeMerchItem = (index) => {
    setFormData(prev => ({
      ...prev,
      merch_items: prev.merch_items.filter((_, i) => i !== index)
    }));
  };

  const updateMerchItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      merch_items: prev.merch_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter your band name');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter a contact email');
      return;
    }
    if (!formData.bio.trim()) {
      toast.error('Please enter a bio');
      return;
    }
    if (formData.genre_tags.length === 0) {
      toast.error('Please select at least one genre');
      return;
    }
    if (!formData.embed_tracks[0]?.embed_url?.trim()) {
      toast.error('Please add at least one track');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Filter out empty items
      const cleanedData = {
        ...formData,
        image_urls: formData.image_urls.filter(url => url.trim()),
        merch_items: formData.merch_items.filter(item => item.url.trim()),
        embed_tracks: formData.embed_tracks.filter(track => track.embed_url.trim()),
        upcoming_shows: formData.upcoming_shows.filter(show => show.venue.trim() || show.date.trim())
      };
      
      const res = await fetch(`${API}/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(cleanedData)
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = 'Submission failed';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      toast.success('Application submitted! We\'ll review it soon.');
      navigate('/profile');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary font-accent text-3xl">LOADING...</div>
      </div>
    );
  }

  // Check for pending application
  const pendingApp = existingApplications.find(a => a.status === 'pending');
  const approvedApp = existingApplications.find(a => a.status === 'approved');

  if (approvedApp) {
    return (
      <div className="min-h-screen py-12" data-testid="already-approved">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
          <h1 className="font-accent text-4xl text-foreground mb-4">YOU'RE APPROVED!</h1>
          <p className="font-mono text-muted-foreground mb-8">
            Your band has been approved. Manage your profile from the Band Dashboard.
          </p>
          <Button 
            onClick={() => navigate('/band-dashboard')}
            className="border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase"
          >
            Go to Band Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (pendingApp) {
    return (
      <div className="min-h-screen py-12" data-testid="pending-application">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Clock className="w-16 h-16 text-primary mx-auto mb-6" />
          <h1 className="font-accent text-4xl text-foreground mb-4">APPLICATION PENDING</h1>
          <p className="font-mono text-muted-foreground mb-4">
            Your application for <span className="text-primary">{pendingApp.name}</span> is being reviewed.
          </p>
          <p className="font-mono text-xs text-muted-foreground">
            Submitted: {new Date(pendingApp.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="band-apply-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="font-accent text-5xl text-foreground mb-4">APPLY AS A BAND</h1>
          <p className="font-mono text-sm text-muted-foreground uppercase tracking-widest">
            Join the underground • Get discovered
          </p>
        </div>

        {/* Rejected Applications Notice */}
        {existingApplications.some(a => a.status === 'rejected') && (
          <div className="mb-8 border-2 border-destructive bg-destructive/10 p-4">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-mono text-sm text-destructive">
                  A previous application was rejected. You can submit a new one.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Application Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="border-2 border-border bg-card p-6 space-y-6">
            <h2 className="font-sans text-xl font-bold border-b border-border pb-4">
              Basic Information
            </h2>
            
            <div className="space-y-2">
              <Label className="font-mono text-sm uppercase">Band Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Your band name"
                className="bg-input border-2 border-border font-mono"
                data-testid="band-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="font-mono text-sm uppercase">Contact Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="band@email.com"
                className="bg-input border-2 border-border font-mono"
                data-testid="band-email-input"
              />
              <p className="font-mono text-xs text-muted-foreground">
                We'll use this to contact you about your application
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="font-mono text-sm uppercase">Bio *</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell us about your band, your sound, your story..."
                className="bg-input border-2 border-border font-mono min-h-[120px]"
                data-testid="band-bio-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="font-mono text-sm uppercase">Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="City, State/Country"
                className="bg-input border-2 border-border font-mono"
                data-testid="band-location-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="font-mono text-sm uppercase">Genres * (Select all that apply)</Label>
              <div className="flex flex-wrap gap-2">
                {GENRE_OPTIONS.map((genre) => (
                  <Button
                    key={genre}
                    type="button"
                    variant={formData.genre_tags.includes(genre) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleGenre(genre)}
                    className={`font-mono text-xs uppercase ${
                      formData.genre_tags.includes(genre) 
                        ? 'bg-primary text-black border-primary' 
                        : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                    }`}
                    data-testid={`genre-${genre}`}
                  >
                    {genre}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Logo & Images */}
          <div className="border-2 border-border bg-card p-6 space-y-6">
            <h2 className="font-sans text-xl font-bold border-b border-border pb-4">
              Logo & Images
            </h2>
            
            <div className="space-y-4">
              <Label className="font-mono text-sm uppercase">Logo</Label>
              <div className="flex flex-col gap-4">
                {formData.logo_url ? (
                  <div className="flex items-center gap-4">
                    <img src={formData.logo_url} alt="Logo preview" className="w-24 h-24 object-cover border-2 border-primary" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteUploadedImage(formData.logo_url, 'logo')}
                      className="text-destructive hover:text-destructive/80 font-mono text-xs"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-4 items-center">
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-border hover:border-primary px-4 py-3 flex items-center gap-2 transition-colors">
                        <Upload className="w-5 h-5 text-muted-foreground" />
                        <span className="font-mono text-sm text-muted-foreground">
                          {uploading ? 'Uploading...' : 'Upload Logo'}
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={(e) => handleImageUpload(e, 'logo')}
                        className="hidden"
                        disabled={uploading}
                        data-testid="logo-upload-input"
                      />
                    </label>
                    <span className="font-mono text-xs text-muted-foreground">or</span>
                    <Input
                      value={formData.logo_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                      placeholder="Paste URL..."
                      className="bg-input border border-border font-mono flex-1"
                      data-testid="logo-url-input"
                    />
                  </div>
                )}
              </div>
              <p className="font-mono text-xs text-muted-foreground">
                Upload an image or paste a URL. Max 5MB.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="font-mono text-sm uppercase">Band Images (up to 5)</Label>
                {formData.image_urls.filter(u => u).length < 5 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addImage}
                    className="font-mono text-xs uppercase"
                    data-testid="add-image-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Image
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {formData.image_urls.map((url, index) => (
                  <div key={index} className="space-y-2">
                    {url ? (
                      <div className="relative group">
                        <img src={url} alt={`Image ${index + 1}`} className="w-full aspect-square object-cover border-2 border-border" />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => handleDeleteUploadedImage(url, 'image', index)}
                          className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <div className="w-full aspect-square border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center gap-2 transition-colors">
                          <Upload className="w-6 h-6 text-muted-foreground" />
                          <span className="font-mono text-xs text-muted-foreground">
                            {uploading ? 'Uploading...' : 'Upload'}
                          </span>
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={(e) => handleImageUpload(e, 'image', index)}
                          className="hidden"
                          disabled={uploading}
                          data-testid={`image-upload-${index}`}
                        />
                      </label>
                    )}
                    {!url && (
                      <Input
                        value={url}
                        onChange={(e) => updateImage(index, e.target.value)}
                        placeholder="Or paste URL"
                        className="bg-input border border-border font-mono text-xs"
                        data-testid={`image-url-${index}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* External Links */}
          <div className="border-2 border-border bg-card p-6 space-y-6">
            <h2 className="font-sans text-xl font-bold border-b border-border pb-4">
              External Links
            </h2>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="font-mono text-sm uppercase">Website / Streaming</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://..."
                  className="bg-input border-2 border-border font-mono"
                  data-testid="website-input"
                />
              </div>
              
              <div className="space-y-2 sm:col-span-2">
                <Label className="font-mono text-sm uppercase">Donate / Support</Label>
                <Input
                  value={formData.donate_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, donate_link: e.target.value }))}
                  placeholder="https://..."
                  className="bg-input border-2 border-border font-mono"
                  data-testid="donate-input"
                />
              </div>
            </div>
          </div>

          {/* Upcoming Shows */}
          <div className="border-2 border-border bg-card p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <h2 className="font-sans text-xl font-bold flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Upcoming Shows
              </h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  upcoming_shows: [...prev.upcoming_shows, { venue: '', location: '', date: '', time: '', cost: '' }] 
                }))}
                className="font-mono text-xs uppercase"
                data-testid="add-show-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Show
              </Button>
            </div>
            
            <p className="font-mono text-xs text-muted-foreground">
              List your upcoming shows so fans can find you live
            </p>

            {formData.upcoming_shows.map((show, index) => (
              <div key={index} className="border border-border p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-mono text-xs text-primary uppercase">Show {index + 1}</span>
                  {formData.upcoming_shows.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        upcoming_shows: prev.upcoming_shows.filter((_, i) => i !== index)
                      }))}
                      className="text-destructive hover:text-destructive/80"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Venue Name</Label>
                    <Input
                      value={show.venue}
                      onChange={(e) => {
                        const updated = [...formData.upcoming_shows];
                        updated[index].venue = e.target.value;
                        setFormData(prev => ({ ...prev, upcoming_shows: updated }));
                      }}
                      placeholder="The Collective"
                      className="bg-input border border-border font-mono"
                      data-testid={`show-venue-${index}`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Location
                    </Label>
                    <Input
                      value={show.location}
                      onChange={(e) => {
                        const updated = [...formData.upcoming_shows];
                        updated[index].location = e.target.value;
                        setFormData(prev => ({ ...prev, upcoming_shows: updated }));
                      }}
                      placeholder="Sedona, AZ"
                      className="bg-input border border-border font-mono"
                      data-testid={`show-location-${index}`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Date</Label>
                    <Input
                      type="date"
                      value={show.date}
                      onChange={(e) => {
                        const updated = [...formData.upcoming_shows];
                        updated[index].date = e.target.value;
                        setFormData(prev => ({ ...prev, upcoming_shows: updated }));
                      }}
                      className="bg-input border border-border font-mono"
                      data-testid={`show-date-${index}`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Time</Label>
                    <Input
                      value={show.time}
                      onChange={(e) => {
                        const updated = [...formData.upcoming_shows];
                        updated[index].time = e.target.value;
                        setFormData(prev => ({ ...prev, upcoming_shows: updated }));
                      }}
                      placeholder="8:00 PM"
                      className="bg-input border border-border font-mono"
                      data-testid={`show-time-${index}`}
                    />
                  </div>
                  
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="font-mono text-xs uppercase flex items-center gap-1">
                      <DollarSign className="w-3 h-3" /> Cost
                    </Label>
                    <Input
                      value={show.cost}
                      onChange={(e) => {
                        const updated = [...formData.upcoming_shows];
                        updated[index].cost = e.target.value;
                        setFormData(prev => ({ ...prev, upcoming_shows: updated }));
                      }}
                      placeholder="$15, Free, $10-20"
                      className="bg-input border border-border font-mono"
                      data-testid={`show-cost-${index}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Merch Items */}
          <div className="border-2 border-border bg-card p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <h2 className="font-sans text-xl font-bold">Merch Links</h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addMerchItem}
                className="font-mono text-xs uppercase"
                data-testid="add-merch-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Merch
              </Button>
            </div>
            
            <p className="font-mono text-xs text-muted-foreground">
              Add links to your merchandise (t-shirts, vinyl, CDs, etc.)
            </p>
            
            {formData.merch_items.map((item, index) => (
              <div key={index} className="border border-border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-muted-foreground uppercase flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4" />
                    Merch Item {index + 1}
                  </span>
                  {formData.merch_items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeMerchItem(index)}
                      className="text-destructive hover:text-destructive/80 h-6 w-6"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Item Name</Label>
                    <Input
                      value={item.name}
                      onChange={(e) => updateMerchItem(index, 'name', e.target.value)}
                      placeholder="T-Shirt, Vinyl, etc."
                      className="bg-input border border-border font-mono"
                      data-testid={`merch-name-${index}`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Link</Label>
                    <Input
                      value={item.url}
                      onChange={(e) => updateMerchItem(index, 'url', e.target.value)}
                      placeholder="https://..."
                      className="bg-input border border-border font-mono"
                      data-testid={`merch-url-${index}`}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase">Description (optional)</Label>
                  <Input
                    value={item.description}
                    onChange={(e) => updateMerchItem(index, 'description', e.target.value)}
                    placeholder="Brief description..."
                    className="bg-input border border-border font-mono"
                    data-testid={`merch-desc-${index}`}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Music Embeds */}
          <div className="border-2 border-border bg-card p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <h2 className="font-sans text-xl font-bold">Music Embeds *</h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTrack}
                className="font-mono text-xs uppercase"
                data-testid="add-track-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Track
              </Button>
            </div>
            
            <p className="font-mono text-xs text-muted-foreground">
              Add embed URLs from Spotify, YouTube, SoundCloud, or Bandcamp
            </p>
            
            <div className="bg-primary/10 border border-primary/30 p-3 rounded space-y-2">
              <p className="font-mono text-xs text-primary">
                <strong>Tip for Spotify tracks:</strong> Click the three dots next to the song → Share → Copy link to Song.
              </p>
              <p className="font-mono text-xs text-primary">
                <strong>Tip for full discography:</strong> Go to your Spotify artist profile → click the three dots (next to "Following") → Share → Copy Artist link. Use the "Album" dropdown option for this, the other options won't work properly.
              </p>
            </div>
            
            {formData.embed_tracks.map((track, index) => (
              <div key={index} className="border border-border p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-muted-foreground uppercase">
                    Track {index + 1}
                  </span>
                  {formData.embed_tracks.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTrack(index)}
                      className="text-destructive hover:text-destructive/80 h-6 w-6"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Title</Label>
                    <Input
                      value={track.title}
                      onChange={(e) => updateTrack(index, 'title', e.target.value)}
                      placeholder="Track title"
                      className="bg-input border border-border font-mono"
                      data-testid={`track-title-${index}`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Type</Label>
                    <select
                      value={track.type}
                      onChange={(e) => updateTrack(index, 'type', e.target.value)}
                      className="w-full bg-input border border-border font-mono text-sm p-2"
                      data-testid={`track-type-${index}`}
                    >
                      <option value="single">Single</option>
                      <option value="ep">EP</option>
                      <option value="album">Album</option>
                      <option value="track">Track</option>
                    </select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase">Embed URL *</Label>
                  <Input
                    value={track.embed_url}
                    onChange={(e) => updateTrack(index, 'embed_url', e.target.value)}
                    placeholder="https://open.spotify.com/..."
                    className="bg-input border border-border font-mono"
                    data-testid={`track-url-${index}`}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase tracking-widest btn-brutal py-6 text-lg"
            data-testid="submit-application-btn"
          >
            {submitting ? 'Submitting...' : 'Submit Application'}
            <Send className="w-5 h-5 ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default BandApply;
